import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle2, Loader2, Rocket, X } from "lucide-react";
import { checkAppAuth, selectAppUser } from "../../redux/appAuthSlice";
import { isUserVerified } from "../../utils/profileHelpers";
import { createBetHouse, fetchOperatorHouses } from "../../redux/operatorConsoleSlice";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import {
  buildCreateBetHousePayload,
  CREATE_STEPS,
  DEFAULT_MIN_BET,
  MIN_HOUSE_ALLOCATION,
  parseDigits,
  QUICK_ALLOCATION_AMOUNTS,
  validateAllocation,
  validateBetSettings,
  validateHouseName,
} from "../../utils/createBetHouseHelpers";
import "../../create-bet-house.css";

const resetForm = () => ({
  step: 1,
  error: "",
  name: "",
  allocation: "",
  minSelections: "2",
  minBet: String(DEFAULT_MIN_BET),
  maxBet: "",
  brandStory: "",
  createdHouseId: null,
  createdHouseName: "",
});

const CreateBetHouseModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const userId = user?._id;
  const userCash = Number(user?.CASH) || 0;

  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [createdHouseId, setCreatedHouseId] = useState(null);
  const [createdHouseName, setCreatedHouseName] = useState("");

  const [name, setName] = useState("");
  const [allocation, setAllocation] = useState("");
  const [minSelections, setMinSelections] = useState("2");
  const [minBet, setMinBet] = useState(String(DEFAULT_MIN_BET));
  const [maxBet, setMaxBet] = useState("");
  const [brandStory, setBrandStory] = useState("");

  const progressPct = useMemo(
    () => ((step - 1) / Math.max(CREATE_STEPS.length - 1, 1)) * 100,
    [step]
  );

  const handleClose = useCallback(() => {
    if (submitting) return;
    const next = resetForm();
    setStep(next.step);
    setError(next.error);
    setName(next.name);
    setAllocation(next.allocation);
    setMinSelections(next.minSelections);
    setMinBet(next.minBet);
    setMaxBet(next.maxBet);
    setBrandStory(next.brandStory);
    setCreatedHouseId(next.createdHouseId);
    setCreatedHouseName(next.createdHouseName);
    setSubmitting(false);
    onClose?.();
  }, [onClose, submitting]);

  useEffect(() => {
    if (!open) return undefined;

    dispatch(checkAppAuth());

    const next = resetForm();
    setStep(next.step);
    setError(next.error);
    setName(next.name);
    setAllocation(next.allocation);
    setMinSelections(next.minSelections);
    setMinBet(next.minBet);
    setMaxBet(next.maxBet);
    setBrandStory(next.brandStory);
    setCreatedHouseId(next.createdHouseId);
    setCreatedHouseName(next.createdHouseName);
    setSubmitting(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) handleClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, handleClose, submitting]);

  const goBack = () => {
    if (step <= 1) {
      handleClose();
      return;
    }
    setError("");
    setStep((prev) => prev - 1);
  };

  const validateStep = useCallback(
    (currentStep) => {
      if (currentStep === 1) return validateHouseName(name);
      if (currentStep === 2) return validateAllocation(allocation, userCash);
      if (currentStep === 3) {
        return validateBetSettings({
          minSelections,
          minBet,
          maxBet,
        });
      }
      return null;
    },
    [name, allocation, userCash, minSelections, minBet, maxBet]
  );

  const submitHouse = async () => {
    if (!userId) {
      setError("You must be signed in to create a bet house.");
      return;
    }
    if (!isUserVerified(user)) {
      setError("Verify your account before creating a bet house.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const payload = buildCreateBetHousePayload({
        userId,
        name,
        amountAllocated: allocation,
        minSelections,
        minBet,
        maxBet,
        brandStory,
      });

      const result = await dispatch(createBetHouse(payload)).unwrap();
      const houseId = result?._id;
      if (!houseId) {
        throw new Error("House was created but no id was returned.");
      }

      setCreatedHouseId(String(houseId));
      setCreatedHouseName(payload.name);
      await Promise.all([
        dispatch(fetchOperatorHouses(userId)),
        dispatch(checkAppAuth()),
      ]);
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "Could not create bet house.");
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = async () => {
    const message = validateStep(step);
    if (message) {
      setError(message);
      return;
    }
    setError("");

    if (step < CREATE_STEPS.length) {
      setStep((prev) => prev + 1);
      return;
    }

    await submitHouse();
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <h2>Name your house</h2>
            <p>This is what bettors see when browsing Bet House.</p>
            <div className="cbh-field">
              <label htmlFor="cbh-name">House name</label>
              <input
                id="cbh-name"
                className="cbh-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Premier Edge Markets"
                maxLength={40}
                autoFocus
              />
              <div className="cbh-char-count">{name.length}/40</div>
            </div>
          </>
        );
      case 2:
        return (
          <>
            <h2>Set your budget</h2>
            <p>
              Allocate Cash from your wallet to back payouts. Minimum{" "}
              {MIN_HOUSE_ALLOCATION.toLocaleString()} Cash.
            </p>
            <div className="cbh-field">
              <label htmlFor="cbh-allocation">Allocation</label>
              <div className="cbh-cash-row">
                <input
                  id="cbh-allocation"
                  className="cbh-input tabular-nums"
                  value={allocation}
                  onChange={(e) => setAllocation(parseDigits(e.target.value))}
                  placeholder={String(MIN_HOUSE_ALLOCATION)}
                  inputMode="numeric"
                />
                <span className="cbh-cash-badge">Cash</span>
              </div>
              <p className="cbh-balance-hint">
                Available: <strong>{formatCash(userCash, { compact: true })}</strong>
              </p>
              <div className="cbh-quick">
                {QUICK_ALLOCATION_AMOUNTS.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    className={allocation === String(amount) ? "cbh-quick-active" : ""}
                    onClick={() => setAllocation(String(amount))}
                  >
                    {amount.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>
          </>
        );
      case 3:
        return (
          <>
            <h2>Bet settings</h2>
            <p>Defaults for tickets placed on your house.</p>
            <div className="cbh-field">
              <label htmlFor="cbh-min-selections">Minimum selections per ticket</label>
              <input
                id="cbh-min-selections"
                className="cbh-input tabular-nums"
                value={minSelections}
                onChange={(e) => setMinSelections(parseDigits(e.target.value))}
                inputMode="numeric"
              />
            </div>
            <div className="cbh-field">
              <label htmlFor="cbh-min-bet">Default minimum bet</label>
              <div className="cbh-cash-row">
                <input
                  id="cbh-min-bet"
                  className="cbh-input tabular-nums"
                  value={minBet}
                  onChange={(e) => setMinBet(parseDigits(e.target.value))}
                  inputMode="numeric"
                />
                <span className="cbh-cash-badge">Cash</span>
              </div>
            </div>
            <div className="cbh-field">
              <label htmlFor="cbh-max-bet">Default maximum bet (optional)</label>
              <div className="cbh-cash-row">
                <input
                  id="cbh-max-bet"
                  className="cbh-input tabular-nums"
                  value={maxBet}
                  onChange={(e) => setMaxBet(parseDigits(e.target.value))}
                  placeholder="No cap"
                  inputMode="numeric"
                />
                <span className="cbh-cash-badge">Cash</span>
              </div>
            </div>
          </>
        );
      case 4:
      default:
        return (
          <>
            <h2>Brand story</h2>
            <p>Optional — tell bettors what makes your house different.</p>
            <div className="cbh-field">
              <label htmlFor="cbh-story">About your house</label>
              <textarea
                id="cbh-story"
                className="cbh-textarea"
                value={brandStory}
                onChange={(e) => setBrandStory(e.target.value)}
                placeholder="Your approach to odds, markets you focus on, or how you run the book…"
                maxLength={1000}
              />
              <div className="cbh-char-count">{brandStory.length}/1000</div>
            </div>
          </>
        );
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="cbh-modal-root" role="dialog" aria-modal="true" aria-labelledby="cbh-modal-title">
      <button
        type="button"
        className="cbh-modal-backdrop"
        aria-label="Close"
        onClick={handleClose}
        disabled={submitting}
      />

      <div className="cbh-modal-sheet">
        {createdHouseId ? (
          <div className="cbh-modal-success">
            <button
              type="button"
              className="cbh-modal-close"
              onClick={handleClose}
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <div className="cbh-success-icon">
              <CheckCircle2 size={28} />
            </div>
            <h2>House created</h2>
            <p>
              <strong>{createdHouseName}</strong> is ready in draft. Add fixtures and go live
              from your operator console.
            </p>
            <button
              type="button"
              className="cbh-btn cbh-btn-primary cbh-btn-full"
              onClick={() => onSuccess?.(createdHouseId)}
            >
              Open operator console
            </button>
          </div>
        ) : (
          <>
            <header className="cbh-modal-head">
              <div className="cbh-modal-head-copy">
                <p className="cbh-eyebrow">
                  <Rocket size={11} />
                  Launch
                </p>
                <h2 id="cbh-modal-title">Create bet house</h2>
                <span>
                  Step {step} of {CREATE_STEPS.length} · {CREATE_STEPS[step - 1]?.title}
                </span>
              </div>
              <button
                type="button"
                className="cbh-modal-close"
                onClick={handleClose}
                aria-label="Close"
                disabled={submitting}
              >
                <X size={16} />
              </button>
            </header>

            <div className="cbh-modal-body">
              <div className="cbh-progress" aria-hidden>
                <div className="cbh-progress-track">
                  <div className="cbh-progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
                <div className="cbh-steps">
                  {CREATE_STEPS.map(({ id, title }) => {
                    const done = step > id;
                    const active = step === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`cbh-step-dot ${active ? "cbh-step-active" : ""} ${done ? "cbh-step-done" : ""}`}
                        onClick={() => {
                          if (done) {
                            setError("");
                            setStep(id);
                          }
                        }}
                        disabled={!done && !active}
                        aria-current={active ? "step" : undefined}
                      >
                        <em>{done ? "✓" : id}</em>
                        <span>{title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="cbh-card cbh-card-flat">
                {error ? <div className="cbh-error">{error}</div> : null}
                {renderStepContent()}
              </div>
            </div>

            <footer className="cbh-modal-foot">
              <button
                type="button"
                className="cbh-btn cbh-btn-ghost"
                onClick={goBack}
                disabled={submitting}
              >
                {step === 1 ? "Cancel" : "Back"}
              </button>
              <button
                type="button"
                className="cbh-btn cbh-btn-primary"
                onClick={goNext}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Creating…
                  </>
                ) : step === CREATE_STEPS.length ? (
                  "Create house"
                ) : (
                  "Continue"
                )}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CreateBetHouseModal;
